import React, { useEffect, useRef, useState } from 'react';
import { FaBuilding, FaChartBar, FaKey, FaSignOutAlt, FaTimesCircle, FaUser } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useU2106HasAiProviders } from '../../../u2106MyAccount/hooks/useU2106HasAiProviders';
import { useU2106SsoEnabled } from '../../../u2106MyAccount/hooks/useU2106SsoEnabled';
import { useU2000AuthContext } from '../../context/auth/U2000UseAuthContext';
import { useU2000FormLeaveGuardContext } from '../../context/formLeaveGuard/U2000FormLeaveGuardContext';
import useU2000Logout from '../../hooks/authentification/U2000UseLogout';
import {
  U2000_USER_MENU_U2106_CHANGEPASSWORD_ROLE,
  U2000_USER_MENU_U2106_FLAGS,
  U2000_USER_MENU_U2106_READ_ROLE,
  U2000_USER_MENU_U3148_READ_ROLE,
  u2000ComputeUserMenuVisibility,
  u2000UserMenuInitials,
} from '../../tools/U2000UserMenuVisibility';
import U2000ActionBtn from '../buttons/U2000ActionBtn';
import U2000BasicDialog from '../dialog/U2000BasicDialog';

interface U2000UserProfilePopupProps {
  isVisible: boolean;
  onClose: () => void;
}

const menuItemClass =
  'w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors';

const U2000UserProfilePopup: React.FC<U2000UserProfilePopupProps> = ({ isVisible, onClose }) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const { auth, hasRole } = useU2000AuthContext();
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const doLogout = useU2000Logout();
  const { requestConfirmedLeave } = useU2000FormLeaveGuardContext();
  const [userName, setUserName] = useState<string>('');
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const hasU2106Read = hasRole(U2000_USER_MENU_U2106_READ_ROLE);
  const hasChangePasswordRole = hasRole(U2000_USER_MENU_U2106_CHANGEPASSWORD_ROLE);
  const aiUsageGateEnabled = U2000_USER_MENU_U2106_FLAGS.aiUsageAvailable && hasU2106Read;
  const { hasAiProviders, ensureLoaded: ensureAiProvidersLoaded } = useU2106HasAiProviders(aiUsageGateEnabled);
  const ssoGateEnabled = U2000_USER_MENU_U2106_FLAGS.changePasswordAvailable && hasChangePasswordRole;
  const { ssoEnabled, ensureLoaded: ensureSsoLoaded } = useU2106SsoEnabled(ssoGateEnabled);

  useEffect(() => {
    if (isVisible && aiUsageGateEnabled) {
      ensureAiProvidersLoaded();
    }
  }, [isVisible, aiUsageGateEnabled, ensureAiProvidersLoaded]);

  useEffect(() => {
    if (isVisible && ssoGateEnabled) {
      ensureSsoLoaded();
    }
  }, [isVisible, ssoGateEnabled, ensureSsoLoaded]);

  // BL-0334 : Mon profil = flag + U2106:READ (pas de gate hasEmployee / GET profile).
  // BL-0339 : Mon organisation = U3148:READ et organizationId de session.
  // BL-0520 : masquer changer MDP si ssoEnabled.
  const visibility = u2000ComputeUserMenuVisibility(
    hasRole(U2000_USER_MENU_U3148_READ_ROLE),
    U2000_USER_MENU_U2106_FLAGS,
    hasChangePasswordRole,
    hasU2106Read,
    false,
    hasAiProviders === true,
    Boolean(auth?.user?.organizationId),
    ssoEnabled === true,
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popupRef.current && event.target instanceof Node && !popupRef.current.contains(event.target) && isVisible) {
        onClose();
      }
    }

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);

  useEffect(() => {
    if (auth?.isAuthenticated) {
      setUserName(auth?.user.firstName + ' ' + auth?.user.lastName);
    }
  }, [auth]);

  const handleNavigate = (path: string) => {
    onClose();
    navigate(path);
  };

  const handleSignOutClick = () => {
    onClose();
    requestConfirmedLeave(() => setIsLogoutConfirmOpen(true));
  };

  const handleLogoutDialogClose = (_reason?: string) => {
    if (!isLoggingOut) {
      setIsLogoutConfirmOpen(false);
    }
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    try {
      await doLogout();
      setIsLogoutConfirmOpen(false);
    } catch (error) {
      console.error(error);
      setIsLogoutConfirmOpen(false);
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <>
      {isVisible && (
        <div
          ref={popupRef}
          className="absolute top-[50px] right-0 z-1000 w-[240px] bg-white border border-gray-200 rounded-xl shadow-xl"
        >
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                {u2000UserMenuInitials(userName)}
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <div className="font-medium text-gray-900 text-sm truncate">{userName}</div>
                <div className="text-xs text-gray-500 truncate">{auth.user.email}</div>
              </div>
            </div>
          </div>

          {visibility.showMenuActions && (
            <div className="py-2">
              {visibility.showMyProfile && (
                <button type="button" className={menuItemClass} onClick={() => handleNavigate('/U2106/profile')}>
                  <FaUser className="w-4 h-4 mr-3 text-gray-500" />
                  {t('userMenu.myProfile')}
                </button>
              )}
              {visibility.showChangePassword && (
                <button type="button" className={menuItemClass} onClick={() => handleNavigate('/U2106/password')}>
                  <FaKey className="w-4 h-4 mr-3 text-gray-500" />
                  {t('userMenu.changePassword')}
                </button>
              )}
              {visibility.showMyAiUsage && (
                <button type="button" className={menuItemClass} onClick={() => handleNavigate('/U2106/ai-usage')}>
                  <FaChartBar className="w-4 h-4 mr-3 text-gray-500" />
                  {t('userMenu.myAiUsage')}
                </button>
              )}
              {visibility.showMyOrganization && (
                <button type="button" className={menuItemClass} onClick={() => handleNavigate('/U3148')}>
                  <FaBuilding className="w-4 h-4 mr-3 text-gray-500" />
                  {t('userMenu.myOrganization')}
                </button>
              )}
            </div>
          )}

          <div className="border-t border-gray-100 py-2">
            <button
              type="button"
              onClick={handleSignOutClick}
              className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors"
            >
              <FaSignOutAlt className="w-4 h-4 mr-3 text-red-500" />
              {t('userMenu.signOut')}
            </button>
          </div>
        </div>
      )}

      <U2000BasicDialog
        isOpen={isLogoutConfirmOpen}
        onClose={handleLogoutDialogClose}
        title={t('userMenu.logoutConfirmTitle')}
        width="w-[420px]"
      >
        <div className="flex flex-col items-center justify-center p-4 text-center">
          <p className="text-sm text-gray-700">{t('userMenu.logoutConfirmMessage')}</p>
        </div>
        <div className="mt-2 flex justify-center gap-x-2 border-t-2 border-gray-300 p-4">
          <U2000ActionBtn
            label={t('userMenu.logoutConfirmAction')}
            icon={FaSignOutAlt}
            handleClick={() => {
              void handleLogoutConfirm();
            }}
            disabled={isLoggingOut}
          />
          <U2000ActionBtn
            label={t('userMenu.logoutConfirmCancel')}
            icon={FaTimesCircle}
            handleClick={() => handleLogoutDialogClose()}
            variant="secondary"
            disabled={isLoggingOut}
          />
        </div>
      </U2000BasicDialog>
    </>
  );
};

export default U2000UserProfilePopup;
